/**
 * Header bar for ApplyRuleBasedSendersSection: title, Select all, AI, New sender.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';
import { ChevronDown, Search, User } from 'lucide-react';

import { Button } from '@/components/ui';
import {
  AiGenerateSendersDropdown,
  type AiGenerateSendersMode,
} from '@/features/campaign-texts/components/AiGenerateSendersDropdown';
import { cn } from '@/lib/utils';

import { AddNewSenderBlock } from './AddNewSenderBlock';

const MAX_SELECTED_SENDERS = 50;

export interface SendersSectionHeaderProps {
  selectedCount: number;
  selectAllLabel: string;
  onSelectAll: () => void;
  isBusy: boolean;
  filteredCount: number;
  dropCount?: number;
  dropLabels?: Array<{ id: string; label: string }>;
  countryKey: string;
  onAiGeneratingChange?: (v: boolean) => void;
  onAiGenerated: (senders: string[] | string[][], gId: string, mode: AiGenerateSendersMode, ms?: number, ids?: string[]) => void;
  showAddNew: boolean;
  onToggleAddNew: () => void;
  search: string;
  onSearchChange: (v: string) => void;
  newSenderValue: string;
  onNewSenderChange: (v: string) => void;
  newSenderError: string | null;
  onAddSender: () => void;
  onPasteAddSenders: (senders: string[]) => void;
}

export const SendersSectionHeader: FC<SendersSectionHeaderProps> = (p) => (
  <>
    <div className="flex items-center justify-between gap-2">
      <h4 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <User className="h-3.5 w-3.5" />
        Senders
        <span
          className={cn(
            'rounded px-1.5 py-0.5 text-xs',
            p.selectedCount > MAX_SELECTED_SENDERS
              ? 'bg-destructive/20 text-destructive font-semibold'
              : 'bg-primary/20 text-primary',
          )}
        >
          {p.selectedCount}/{MAX_SELECTED_SENDERS}
        </span>
      </h4>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 text-xs"
          onClick={p.onSelectAll}
          disabled={p.isBusy || p.filteredCount === 0}
        >
          {p.selectAllLabel}
        </Button>
        {(p.dropCount ?? 0) > 0 ? (
          <AiGenerateSendersDropdown
            countryKey={p.countryKey}
            dropCount={p.dropCount ?? 0}
            dropLabels={p.dropLabels}
            onGeneratingChange={p.onAiGeneratingChange}
            onGenerated={p.onAiGenerated}
          />
        ) : (
          <span className="text-xs text-muted-foreground">
            {p.dropCount === undefined ? 'Checking…' : 'No matching lists'}
          </span>
        )}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-6 gap-1 text-xs"
          onClick={p.onToggleAddNew}
        >
          <ChevronDown className={cn('h-3 w-3 transition-transform', p.showAddNew && 'rotate-180')} />
          New sender
        </Button>
      </div>
    </div>

    <div className="relative">
      <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <input
        className="h-7 w-full rounded-md border border-border bg-background pl-7 pr-2 text-xs outline-none placeholder:text-muted-foreground"
        placeholder="Filter senders..."
        value={p.search}
        onChange={(e) => p.onSearchChange(e.target.value)}
      />
    </div>

    {p.showAddNew && (
      <AddNewSenderBlock
        value={p.newSenderValue}
        onChange={(v) => { p.onNewSenderChange(v); }}
        error={p.newSenderError}
        onAdd={p.onAddSender}
        onPasteAdd={p.onPasteAddSenders}
        disabled={p.isBusy}
      />
    )}
  </>
);
